/** A marca da loja: brasão, nome na aba e ícone do navegador.
 *
 * Nada aqui conhece loja nenhuma. O brasão é o arquivo que o administrador
 * enviou (`brasao_url`, URL assinada do S3); sem ele, fica o esquadro e compasso
 * desenhado no `Icone` — genérico de propósito, porque serve a qualquer loja.
 */

import { useEffect, useState } from 'react';

import { Icone } from './Icone';

const PADRAO = 'Oficina Fraterna';

/** O mínimo para desenhar a marca.
 *
 * É estrutural de propósito: serve tanto à loja de quem está autenticado
 * (`LojaPublica`) quanto à loja administrada pela plataforma
 * (`LojaAdministrada`) — tipos diferentes com a mesma marca dentro.
 */
export interface MarcaDaLoja {
  nome: string;
  nome_curto: string | null;
  brasao_url: string | null;
  slug?: string | null;
}

/** O espaço do superadmin não é loja — é o próprio produto.
 *
 * Lá não há brasão para mostrar nem nome de Loja para dizer: quem entra está
 * administrando a Oficina Fraterna, e é a marca dela que vai na barra. */
export const SLUG_PLATAFORMA = 'plataforma';

export function ehPlataforma(loja: MarcaDaLoja | null | undefined): boolean {
  return loja?.slug === SLUG_PLATAFORMA;
}

/** Nome curto da loja, o inteiro, ou — na plataforma — o nome do produto. */
export function nomeDaLoja(loja: MarcaDaLoja | null | undefined): string {
  if (ehPlataforma(loja)) return PADRAO;
  return loja?.nome_curto ?? loja?.nome ?? PADRAO;
}

export function Brasao({
  loja,
  /** Classe da caixa que já existe no CSS: `sid-gem` ou `login-logo`. */
  caixa,
  /** Tamanho do símbolo genérico, usado quando a loja não tem brasão. */
  tamanho,
}: {
  loja: MarcaDaLoja | null | undefined;
  caixa: string;
  tamanho: number;
}) {
  const [falhou, setFalhou] = useState(false);

  // A URL assinada expira; se trocar, vale tentar de novo.
  useEffect(() => setFalhou(false), [loja?.brasao_url]);

  // Na plataforma a marca é a do produto, em negativo: a caixa da marca é
  // sempre escura no sistema (barra lateral e cabeçalho do celular), e a placa
  // branca do símbolo é o que assenta ali.
  if (ehPlataforma(loja)) {
    return (
      <div className={`${caixa} com-brasao`}>
        <img src="/marca/simbolo-negativa.svg" alt="Oficina Fraterna" decoding="async" />
      </div>
    );
  }

  if (loja?.brasao_url && !falhou) {
    return (
      // `com-brasao` tira o fundo e o anel dourado da caixa: o brasão traz as
      // cores dele, e um gradiente azul por baixo sujaria o desenho.
      <div className={`${caixa} com-brasao`}>
        <img
          src={loja.brasao_url}
          alt={`Brasão da ${nomeDaLoja(loja)}`}
          decoding="async"
          onError={() => setFalhou(true)}
        />
      </div>
    );
  }

  return (
    <div className={caixa}>
      <Icone nome="marca" tamanho={tamanho} />
    </div>
  );
}

/** Põe o nome da loja na aba e o brasão no ícone do navegador.
 *
 * O `index.html` traz o título e o ícone da **Oficina Fraterna** — tem de
 * trazer, porque são servidos antes de a API dizer qual é a loja. Assim que ela
 * diz, os dois passam a ser da Loja: é o que o guia da marca pede, que em tela
 * o brasão da Loja seja o protagonista. Sem brasão enviado, o Fr∴ continua.
 */
export function useMarcaDaLoja(loja: MarcaDaLoja | null | undefined): void {
  const nome = nomeDaLoja(loja);
  const brasao = loja?.brasao_url ?? null;

  useEffect(() => {
    document.title = nome === PADRAO ? PADRAO : `${nome} · ${PADRAO}`;
  }, [nome]);

  useEffect(() => {
    if (!brasao) return;
    // São vários `link[rel=icon]` (SVG, 32 e 16 px): trocar só o primeiro
    // deixava o navegador escolher um PNG do produto e o brasão nunca aparecia.
    const links = [...document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]')];
    if (links.length === 0) return;

    const antes = links.map((l) => ({ l, href: l.href, type: l.type, sizes: l.getAttribute('sizes') }));
    for (const l of links) {
      l.type = 'image/png';
      l.removeAttribute('sizes'); // o brasão serve a qualquer tamanho
      l.href = brasao;
    }
    // Ao sair (logout, por exemplo) o ícone do produto volta — a próxima loja
    // pode ser outra, e um brasão sobrando na aba seria da loja errada.
    return () => {
      for (const a of antes) {
        a.l.href = a.href;
        a.l.type = a.type;
        if (a.sizes) a.l.setAttribute('sizes', a.sizes);
      }
    };
  }, [brasao]);
}
